/**
 * DaysOfRunway v5 — "Payday Check"
 *
 * Answers ONE question, in plain language: "Will I make it to my next paycheck?"
 *
 * Hero:    a verdict + the actual € amount you'll have (or be short) AT payday
 * Bridge:  visual coverage from today → payday
 * Stats:   the three honest inputs (cash now · burn/day · days to payday)
 *
 * No more abstract "X days left". The number you see is the answer to your question:
 *   COVERED → "+€340 spare at payday on Apr 30"
 *   SHORT   → "−€180 short · cash runs out Apr 25"
 */
import React, { useMemo } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { CheckCircle, AlertTriangle, TrendingUp, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useBudget } from '../../contexts/BudgetContext';
import { useThemeContext } from '../../contexts/ThemeContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useDateFilter } from '../../contexts/DateFilterContext';
import { realTransactions } from '../../utils/transactionFilters';

const STATUS = {
  surplus: { fg: '#10b981', label: 'Covered',  hero: 'COVERED' },
  tight:   { fg: '#f59e0b', label: 'Tight',    hero: 'TIGHT'   },
  short:   { fg: '#ef4444', label: 'Short',    hero: 'SHORT'   },
  idle:    { fg: '#64748b', label: 'No data',  hero: '—'       },
};

function normalize(s) {
  return String(s || '').trim().toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ');
}
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function addDays(d, n) { return new Date(d.getTime() + n * 86400000); }

/**
 * Detect the user's next paycheck by clustering recurring incomes.
 * Returns { date, amount, source } where source ∈ 'detected' | 'inferred' | 'fallback'.
 */
function detectNextPayday(transactions, today = new Date()) {
  const horizon = addDays(today, -120);
  // `transactions` is already filtered to real (non-template) txs by the caller.
  const incomes = (transactions || [])
    .filter(t => t.type === 'income' && t.date && new Date(t.date) <= today)
    .filter(t => new Date(t.date) >= horizon)
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  if (!incomes.length) return { date: endOfMonth(today), source: 'fallback', amount: 0 };

  const groups = {};
  incomes.forEach(tx => {
    const k = normalize(tx.description || tx.merchantName || tx.payee || 'income');
    (groups[k] = groups[k] || []).push(tx);
  });

  let best = null;
  Object.values(groups).forEach(txs => {
    if (txs.length < 2) return;
    const amts = txs.map(t => Math.abs(Number(t.amount) || 0));
    const avg = amts.reduce((a, b) => a + b, 0) / amts.length;
    if (!amts.every(a => Math.abs(a - avg) <= avg * 0.10)) return;
    const ints = [];
    for (let i = 1; i < txs.length; i++) {
      ints.push((new Date(txs[i].date) - new Date(txs[i - 1].date)) / 86400000);
    }
    const avgI = ints.reduce((a, b) => a + b, 0) / ints.length;
    if (avgI < 6 || avgI > 40) return;
    const last = new Date(txs[txs.length - 1].date);
    let next = addDays(last, avgI);
    if (next < today) {
      const cycles = Math.ceil((today - last) / 86400000 / avgI);
      next = addDays(last, cycles * avgI);
    }
    const score = txs.length * 10 + avg / 100;
    if (!best || score > best.score) best = { date: next, amount: avg, score };
  });
  if (best) return { date: best.date, source: 'detected', amount: best.amount };

  // Fallback: last income within 35 days, assume monthly cadence.
  const recent = incomes.filter(t => new Date(t.date) >= addDays(today, -35));
  if (recent.length) {
    const top = recent.reduce((a, b) => Math.abs(b.amount) > Math.abs(a.amount) ? b : a);
    let next = addDays(new Date(top.date), 30);
    if (next < today) next = addDays(today, 7);
    return { date: next, source: 'inferred', amount: Math.abs(top.amount) };
  }
  return { date: endOfMonth(today), source: 'fallback', amount: 0 };
}

function fmtMoney(n, sym) {
  const v = Math.abs(n || 0);
  let body;
  if (v >= 1e6) body = `${(v / 1e6).toFixed(1)}M`;
  else if (v >= 1000) body = `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k`;
  else body = `${Math.round(v)}`;
  return `${sym}${body}`;
}
function fmtSigned(n, sym) {
  const sign = n >= 0 ? '+' : '−';
  return `${sign}${fmtMoney(n, sym)}`;
}
function fmtDate(d, locale) {
  try {
    return new Intl.DateTimeFormat(locale || 'en', { month: 'short', day: 'numeric' }).format(d);
  } catch { return d.toDateString(); }
}

export default function DaysOfRunway() {
  const { t, i18n } = useTranslation();
  const { themeMode } = useThemeContext();
  const { getCurrencySymbol } = useCurrency();
  const sym = getCurrencySymbol?.() || '€';
  const { transactions = [], loading = false } = useBudget();
  const {
    selectedMonth, selectedYear, isCurrentMonth,
    filterTransactionsByMonth,
  } = useDateFilter();
  const isDark = themeMode !== 'light';

  const data = useMemo(() => {
    const realToday = new Date();
    // Window: the dashboard's selected month
    const monthStart = new Date(selectedYear, selectedMonth, 1);
    const monthEnd   = new Date(selectedYear, selectedMonth + 1, 0);
    const isFuture = realToday < monthStart;
    // "Today" within the selected window: real today if current month,
    // last day if past month, first day if future month
    const today = isCurrentMonth
      ? realToday
      : (realToday > monthEnd ? monthEnd : monthStart);
    const daysInMonth = monthEnd.getDate();
    const daysElapsed = isCurrentMonth
      ? Math.max(1, Math.min(daysInMonth, Math.ceil((today - monthStart) / 86400000) + 1))
      : daysInMonth; // past = full month; future caller handles via isFuture

    // Exclude recurring TEMPLATES — they're blueprints, not real money movements.
    const allRealTxs = realTransactions(transactions);
    // Selected-month transactions (uses budgetMonth-aware filter from DateFilterContext).
    // Only clamp by date for the CURRENT month (live ticking). Past months use the full
    // month so figures match Net Balance / Month Pace exactly, including transactions
    // whose date spilled into the next month but whose budgetMonth belongs here.
    let monthTxs = filterTransactionsByMonth(allRealTxs);
    if (isCurrentMonth) {
      monthTxs = monthTxs.filter(tx => tx.date && new Date(tx.date) <= today);
    }

    // ── Cash now = income RECEIVED in window − expenses spent in window
    const monthIncome = monthTxs.filter(tx => tx.type === 'income')
      .reduce((s, tx) => s + Math.abs(Number(tx.amount) || 0), 0);
    const monthExpenses = monthTxs.filter(tx => tx.type === 'expense' || !tx.type)
      .reduce((s, tx) => s + Math.abs(Number(tx.amount) || 0), 0);
    const cashNow = monthIncome - monthExpenses;

    // ── Burn rate: window expenses ÷ days elapsed in window
    const dailyBurn = monthExpenses / daysElapsed;

    // ── Empty future month → idle
    if (isFuture && monthIncome === 0 && monthExpenses === 0) {
      return {
        cashNow: 0, dailyBurn: 0, payday: { date: monthEnd, source: 'fallback', amount: 0 },
        daysToPay: daysInMonth, buffer: 0, daysCashLasts: 0, runsOutDate: null,
        status: 'idle', coverage: 0, hasIncome: false,
        mode: 'future',
      };
    }

    // ── Past month: show ACTUAL outcome, not a fake forecast
    if (!isCurrentMonth) {
      // Net at month-end is what the user already lived through
      const netAtEnd = cashNow;
      let status;
      if (monthIncome === 0 && monthExpenses === 0) status = 'idle';
      else if (netAtEnd >= dailyBurn * 7)            status = 'surplus';
      else if (netAtEnd >= 0)                        status = 'tight';
      else                                            status = 'short';
      return {
        cashNow, dailyBurn, monthIncome, monthExpenses,
        payday: { date: monthEnd, source: 'closed', amount: 0 },
        daysToPay: daysInMonth,
        buffer: netAtEnd, // hero shows the final net for the closed month
        daysCashLasts: dailyBurn > 0 ? Math.max(0, Math.floor(monthIncome / dailyBurn)) : Infinity,
        runsOutDate: null,
        status, coverage: 100, hasIncome: monthIncome > 0,
        mode: 'closed',
      };
    }

    // ── Current month: live forecast to next payday
    const payday = detectNextPayday(allRealTxs, today);
    const daysToPay = Math.max(1, Math.ceil((payday.date - today) / 86400000));
    const burnUntilPay = dailyBurn * daysToPay;
    const buffer = cashNow - burnUntilPay;

    const daysCashLasts = dailyBurn > 0
      ? Math.max(0, Math.floor(cashNow / dailyBurn))
      : Infinity;
    const runsOutDate = (daysCashLasts !== Infinity && daysCashLasts < daysToPay)
      ? addDays(today, daysCashLasts)
      : null;

    let status;
    if (dailyBurn === 0 && cashNow === 0 && monthIncome === 0) status = 'idle';
    else if (cashNow <= 0) status = 'short';
    else if (buffer >= dailyBurn * 7) status = 'surplus';
    else if (buffer >= 0)             status = 'tight';
    else                              status = 'short';

    const coverage = daysToPay > 0
      ? Math.min(100, (Math.min(daysCashLasts, daysToPay) / daysToPay) * 100)
      : 0;

    return {
      cashNow, dailyBurn, payday, daysToPay,
      buffer, daysCashLasts, runsOutDate, status, coverage,
      hasIncome: monthIncome > 0,
      mode: 'live',
    };
  }, [transactions, selectedMonth, selectedYear, isCurrentMonth, filterTransactionsByMonth]);

  const c = STATUS[data.status];
  const muted    = isDark ? 'rgba(248,250,252,0.45)' : '#94a3b8';
  const textSub  = isDark ? 'rgba(248,250,252,0.68)' : '#475569';
  const textMain = isDark ? '#f8fafc' : '#0f172a';
  const trackBg  = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)';
  const cardBg   = isDark ? 'rgba(255,255,255,0.04)' : '#ffffff';
  const cardBorder = isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)';
  const divider = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)';

  // ── Hero copy: the answer, in plain words
  const isClosed = data.mode === 'closed';
  const isFuture = data.mode === 'future';
  const covered = data.status !== 'short';
  const heroAmount = data.status === 'idle' ? '—' : fmtSigned(data.buffer, sym);
  const heroSub = (() => {
    if (data.status === 'idle') {
      return isFuture
        ? t('runway.future', 'No data yet for this month.')
        : t('runway.idle', 'Add transactions to see your payday outlook.');
    }
    if (isClosed) {
      // Past month: state the actual outcome
      const monthLabel = new Intl.DateTimeFormat(i18n?.language || 'en',
        { month: 'long', year: 'numeric' }).format(new Date(selectedYear, selectedMonth, 1));
      return data.buffer >= 0
        ? t('runway.closedSurplus', '{{m}} closed with money left over.', { m: monthLabel })
        : t('runway.closedShort',   '{{m}} closed in the red.', { m: monthLabel });
    }
    if (data.dailyBurn === 0)    return t('runway.noBurn', 'No spending logged in 30 days.');
    if (covered) {
      return t('runway.heroCovered', 'spare at payday on {{date}}',
        { date: fmtDate(data.payday.date, i18n?.language) });
    }
    return data.runsOutDate
      ? t('runway.heroShortDate', 'short · cash runs out {{date}}',
          { date: fmtDate(data.runsOutDate, i18n?.language) })
      : t('runway.heroShort', 'short of payday on {{date}}',
          { date: fmtDate(data.payday.date, i18n?.language) });
  })();

  // ── Verdict (one-liner explaining the math)
  const verdictText = (() => {
    if (data.status === 'idle') {
      return isFuture
        ? t('runway.verdictFuture', 'Future month — projections appear once data lands here.')
        : t('runway.verdictIdle', 'Log income and expenses to forecast payday coverage.');
    }
    if (isClosed) {
      return data.buffer >= 0
        ? t('runway.verdictClosedSurplus',
            'Net positive: kept {{amt}} after expenses.', { amt: fmtMoney(data.buffer, sym) })
        : t('runway.verdictClosedShort',
            'Net negative: spent {{amt}} more than earned.', { amt: fmtMoney(data.buffer, sym) });
    }
    if (data.dailyBurn === 0)    return t('runway.verdictNoBurn', 'No spending in 30 days — runway is essentially unlimited.');
    if (covered) {
      const weeks = data.dailyBurn > 0 ? Math.floor(data.buffer / (data.dailyBurn * 7)) : 0;
      return weeks >= 1
        ? t('runway.verdictSurplus', "You'll reach payday with about {{w}} week(s) of extra runway.", { w: weeks })
        : t('runway.verdictTight', "You'll reach payday but the buffer is thin — keep an eye on spending.");
    }
    const cutPerDay = Math.ceil(Math.abs(data.buffer) / Math.max(1, data.daysToPay));
    return t('runway.verdictShort',
      'Cut about {{amt}}/day to make it to payday — current pace falls short.',
      { amt: fmtMoney(cutPerDay, sym) });
  })();

  if (loading) {
    return (
      <Box sx={{ background: cardBg, border: cardBorder, borderRadius: '16px', p: '20px',
                 display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <Skeleton width={140} height={12} />
        <Skeleton width={180} height={42} />
        <Skeleton height={24} />
        <Skeleton width="80%" height={13} />
      </Box>
    );
  }

  return (
    <Box sx={{
      background: cardBg,
      backdropFilter: 'blur(20px)',
      border: cardBorder,
      borderRadius: '16px',
      p: '20px',
      display: 'flex',
      flexDirection: 'column',
      gap: '14px',
      boxShadow: isDark ? '0 4px 24px rgba(0,0,0,0.25)' : '0 2px 12px rgba(0,0,0,0.05)',
    }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.10em',
                          textTransform: 'uppercase', color: muted, flex: 1 }}>
          {t('runway.title', 'Payday Check')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: c.fg,
                     boxShadow: `0 0 6px ${c.fg}` }} />
          <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.07em',
                            textTransform: 'uppercase', color: c.fg }}>
            {isClosed
              ? t('runway.statusClosed', 'Closed')
              : isFuture
                ? t('runway.statusUpcoming', 'Upcoming')
                : t(`runway.status.${data.status}`, c.label)}
          </Typography>
        </Box>
      </Box>

      {/* ── Hero: the answer ── */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
          <Typography sx={{
            fontSize: 34, fontWeight: 800, lineHeight: 1,
            letterSpacing: '-0.04em', color: c.fg, fontVariantNumeric: 'tabular-nums',
          }}>
            {heroAmount}
          </Typography>
          <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.09em',
                            textTransform: 'uppercase', color: c.fg }}>
            {c.hero}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 12, color: textSub, mt: '6px', lineHeight: 1.4 }}>
          {heroSub}
        </Typography>
      </Box>

      {/* ── Payday Bridge bar ── */}
      <Box>
        <Box sx={{ position: 'relative', height: 30 }}>
          <Box sx={{ position: 'absolute', left: 0, right: 0, top: '12px', height: 6,
                     background: trackBg, borderRadius: '3px' }} />

          {/* Fill: portion of today→payday window your cash actually covers */}
          <Box sx={{
            position: 'absolute', left: 0, top: '12px', height: 6,
            width: `${data.coverage}%`,
            background: covered
              ? `linear-gradient(90deg, ${c.fg}bb, ${c.fg})`
              : `linear-gradient(90deg, ${c.fg}bb, #ef4444)`,
            borderRadius: '3px',
            boxShadow: covered ? `0 0 10px ${c.fg}44` : 'none',
            transition: 'width 0.6s cubic-bezier(0.4,0,0.2,1)',
          }} />

          {/* "EMPTY" marker when cash runs dry before payday */}
          {!covered && data.coverage > 8 && data.coverage < 94 && (
            <Box sx={{ position: 'absolute', left: `${data.coverage}%`,
                       transform: 'translateX(-50%)', top: '6px' }}>
              <Box sx={{ width: 2, height: 18, background: '#ef4444',
                         borderRadius: '1px', mx: 'auto' }} />
              <Typography sx={{
                position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
                fontSize: 8, fontWeight: 800, color: '#ef4444', letterSpacing: '0.05em',
                textTransform: 'uppercase', whiteSpace: 'nowrap', mb: '1px',
              }}>{t('runway.empty', 'empty')}</Typography>
            </Box>
          )}

          {/* Today dot */}
          <Box sx={{
            position: 'absolute', left: 0, top: '9px',
            width: 12, height: 12, borderRadius: '50%',
            background: isDark ? '#1e293b' : '#fff',
            border: `2px solid ${isDark ? 'rgba(255,255,255,0.3)' : '#94a3b8'}`,
            transform: 'translateX(-1px)',
          }} />

          {/* Payday flag */}
          <Box sx={{
            position: 'absolute', right: 0, top: '8px',
            width: 14, height: 14, borderRadius: '50%',
            background: covered ? c.fg : '#ef4444',
            border: `2px solid ${isDark ? '#1e293b' : '#fff'}`,
            boxShadow: covered ? `0 0 8px ${c.fg}66` : '0 0 8px #ef444466',
            transform: 'translateX(1px)',
          }} />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: '3px' }}>
          <Typography sx={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
                            textTransform: 'uppercase', color: muted }}>
            {isClosed
              ? new Intl.DateTimeFormat(i18n?.language || 'en', { month: 'short', day: 'numeric' })
                  .format(new Date(selectedYear, selectedMonth, 1)).toUpperCase()
              : t('runway.today', 'Today')}
          </Typography>
          <Typography sx={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            color: covered ? c.fg : '#ef4444' }}>
            {isClosed
              ? `${t('runway.monthEnd', 'Month end')} · ${fmtDate(data.payday.date, i18n?.language).toUpperCase()}`
              : `${t('runway.payday', 'Payday')} · ${fmtDate(data.payday.date, i18n?.language).toUpperCase()}`}
          </Typography>
        </Box>
      </Box>

      {/* ── Three honest inputs (closed = retrospective; live = forecast) ── */}
      <Box sx={{ display: 'flex', gap: '8px', pt: '4px' }}>
        <StatPill
          label={isClosed ? t('runway.income', 'Income') : t('runway.cashNow', 'Cash now')}
          value={fmtMoney(isClosed ? data.monthIncome : data.cashNow, sym)}
          isDark={isDark} accent={textMain} muted={muted}
        />
        <StatPill
          label={isClosed ? t('runway.expenses', 'Expenses') : t('runway.dailyBurn', 'Daily burn')}
          value={isClosed
            ? fmtMoney(data.monthExpenses, sym)
            : (data.dailyBurn > 0 ? `${fmtMoney(data.dailyBurn, sym)}/d` : '—')}
          isDark={isDark} accent={textMain} muted={muted}
        />
        <StatPill
          label={isClosed ? t('runway.net', 'Net') : t('runway.toPayday', 'To payday')}
          value={isClosed ? fmtSigned(data.buffer, sym) : `${data.daysToPay}d`}
          isDark={isDark} accent={textMain} muted={muted}
        />
      </Box>

      {/* ── Verdict ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '7px', pt: '10px',
                 borderTop: `1px solid ${divider}` }}>
        {data.status === 'surplus'
          ? <TrendingUp size={13} color={c.fg} style={{ flexShrink: 0, marginTop: 2 }} />
          : data.status === 'tight'
          ? <CheckCircle size={13} color={c.fg} style={{ flexShrink: 0, marginTop: 2 }} />
          : data.status === 'idle'
          ? <Sparkles size={13} color={c.fg} style={{ flexShrink: 0, marginTop: 2 }} />
          : <AlertTriangle size={13} color={c.fg} style={{ flexShrink: 0, marginTop: 2 }} />}
        <Typography sx={{ fontSize: 12.5, fontWeight: 500, lineHeight: 1.4, color: textSub }}>
          {verdictText}
        </Typography>
      </Box>
    </Box>
  );
}

function StatPill({ label, value, isDark, accent, muted }) {
  return (
    <Box sx={{
      flex: 1, minWidth: 0,
      background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.025)',
      border: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.04)',
      borderRadius: '10px', px: '10px', py: '8px',
      display: 'flex', flexDirection: 'column', gap: '2px',
    }}>
      <Typography sx={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.07em',
                        textTransform: 'uppercase', color: muted, lineHeight: 1.1 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 800, color: accent,
                        fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  );
}
